import * as R from 'ramda'

export const defaultColours = {
    'Text Primary': '#102E4A',
    'Text Secondary': '#FFFFFF',
    'Text 3rd': '#33E1D5',
    // years
    2: '#A11B9B',
    1: '#2B3C44',
    0: '#102E4A',
    '-1': '#725AFF',
    '-2': '#AA9CFF',
    '-3': '#D8D3F5',
    '-4': '#AFAACA',
    '-5': '#7B75A1',
    '-6': '#725AFF',
    '-7': '#AA9CFF',
    '-8': '#D8D3F5',
    '-9': '#AFAACA',
    '-10': '#7B75A1',

    'Global Explori Benchmark': '#0DAAC9',
    Industry: '#0DAAC9',
    Company: '#B864DF',
    Division: '#FC73AC',
    'Current Division': '#FC73AC',
    Portfolio: '#FFAACF',
    'Current Portfolio': '#FFAACF',
    Sector: '#33E1D5',

    // added for reports
    Average: '#33E1D5',

    'Custom 1': '#4FFFD6',
    'Custom 2': '#9BFFFD',

    'Red (1 / 5)': '#E8555F',
    'Red (2 / 5)': '#F5A161',
    'Amber (3 / 5)': '#FAEA4C',
    'Green (4 / 5)': '#9EC758',
    'Green (5 / 5)': '#59B363',

    'Grey (N/A)': '#EFF4F6',
}

export const defaultGlobalColours = [
    '#102E4A',
    '#725AFF',
    '#AA9CFF',
    '#D8D3F5',
    '#AFAACA',
    '#7B75A1',
    '#725AFF',
    '#AA9CFF',
    '#D8D3F5',
    '#AFAACA',
    '#7B75A1',
]

const is4DigitNumber = (str) => /^\d{4}$/.test(str)

const getCurrentYear = () => new Date().getFullYear()

const getColour = (seriesName, seriesIndex) => {
    if (!process.env.USE_COLOUR_SCHEMA_V2) return undefined
    if (is4DigitNumber(parseInt(seriesName, 10))) return defaultColours[parseInt(seriesName, 10) - getCurrentYear()]
    if (defaultColours[seriesName]) return defaultColours[seriesName]
    if (seriesName && seriesName.includes(' (') && defaultColours[seriesName.split(' (')[0]]) {
        return defaultColours[seriesName.split(' (')[0]]
    }
    if (seriesIndex || seriesIndex === 0) return defaultColours[0 - seriesIndex]
    return undefined
}

const labelFormatter = (format, dataLabel, optionLabel, decimals) => {
    let formatPreString = ''
    let formatPostSring = ''
    if (format && format !== 'numeric') {
        formatPreString = format !== '%' ? format : ''
        formatPostSring = format === '%' ? format : ''
    }

    if (dataLabel && optionLabel) {
        return (params) => `${params.name}: ${formatPreString}${params.value.toFixed(decimals)}${formatPostSring}`
    }
    if (dataLabel) {
        return (params) => `${formatPreString}${params.value.toFixed(decimals)}${formatPostSring}`
    }
    if (optionLabel) {
        return (params) => `${params.name}`
    }

    return (params) => `${params.name}`
}

const pieRenderSettings = (widget, dashStore) => {
    const { appStore } = dashStore
    const { langcode } = appStore
    const layoutEdits = []
    const compact = R.pathOr(Number(R.pathOr(2, ['width'], widget)) < 4 ? 'vertical' : 'horizontal',
        ['widgetRenderSettings', 'orientation'],
        widget) === 'vertical'

    const legendSetting = R.pathOr(true, ['widgetRenderSettings', 'showLegend'], widget)
    const defaultRadius = widget.chartType === 'donut' ? ['30%', '50%'] : '50%'
    const noLegendRadius = widget.chartType === 'donut' ? ['45%', '65%'] : '65%'
    const colors = R.pathOr(defaultGlobalColours, ['widgetRenderSettings', 'colors'], widget)

    const layout = compact ? [
        { path: ['tooltip', 'position'], value: ['20%', '40%'] },
        {
            path: ['series', 0, 'center'],
            value: legendSetting ? ['50%', '30%'] : ['50%', '50%'],
        },
        {
            path: ['series', 0, 'radius'],
            value: legendSetting ? defaultRadius : noLegendRadius,
        },
        {
            path: ['legend', 'bottom'],
            value: 50,
        },
        {
            path: ['legend', 'show'],
            value: legendSetting,
        },
    ] : [
        { path: ['tooltip', 'position'], value: ['20%', '40%'] },
        {
            path: ['legend', 'show'],
            value: legendSetting,
        },
    ]

    layoutEdits.push(...layout)

    const showDataLabels = R.pathOr(false, ['widgetRenderSettings', 'showDataLabels'], widget)
    const showOptionLabels = R.pathOr(false, ['widgetRenderSettings', 'showOptionLabels'], widget)
    const format = R.pathOr(false, ['widgetRenderSettings', 'format'], widget)
    const currency = R.pathOr('USD', ['widgetRenderSettings', 'currency'], widget)
    const decimals = R.pathOr(0, ['widgetRenderSettings', 'dataDecimals'], widget)

    if (showDataLabels || showOptionLabels) {
        const formatter = labelFormatter(
            format === 'currency' ? dashStore.getCurrencySymbol(currency) : format,
            showDataLabels,
            showOptionLabels,
            decimals,
        )

        layoutEdits.push(
            {
                path: ['series', 0, 'label'],
                value: {
                    show: true,
                    overflow: 'break',
                    position: R.pathOr('inside', ['widgetRenderSettings', 'labelPosition'], widget),
                    fontFamily: '"Noto Sans", "Noto Sans SC", "Noto Sans Arabic", sans-serif',
                    fontWeight: 700,
                    lineHeight: 18,
                    fontSize: 12,
                    width: 100,
                    formatter,
                },
            },
            {
                path: ['series', 0, 'labelLayout', 'hideOverlap'],
                value: R.pathOr(false, ['widgetRenderSettings', 'hideOverlapLabels'], widget),
            },
        )
    }
    const showTitle = R.pathOr(false, ['widgetRenderSettings', 'showTitle'], widget)
    const title = R.pathOr(false, ['widgetRenderSettings', 'title'], widget)

    if (showTitle) {
        layoutEdits.push(
            {
                path: ['title'],
                value: {
                    text: R.pathOr(R.pathOr({ text: '' }, ['en_GB'], title), [langcode], title).text,
                    left: 'left',
                    show: true,
                    textStyle: {
                        fontWeight: 'bold',
                        fontSize: 16,
                        color: '#102E4A',
                    },
                },
            },
        )
    }

    if (colors) {
        layoutEdits.push({
            path: ['color'],
            value: colors,
        })
    }

    return { layoutEdit: layoutEdits }
}

const columnRenderSettings = (widget, dashStore, xAxisLabels, fData) => {
    const { appStore } = dashStore
    const { langcode } = appStore
    const { chartType } = widget
    const layoutEdits = []
    const isBar = chartType === 'bar' || chartType === 'stackedBar'
    const isLine = chartType === 'line'
    const isStacked = chartType.includes('stacked')
    const yaxis = isBar ? 'xAxis' : 'yAxis'
    const xaxis = isBar ? 'yAxis' : 'xAxis'
    const rotateX = R.pathOr(!isBar && xAxisLabels?.length > 8, ['widgetRenderSettings', 'rotateXAxisLabels'], widget)
    const legendSetting = R.pathOr(true, ['widgetRenderSettings', 'showLegend'], widget)
    const showDataLabels = R.pathOr(false, ['widgetRenderSettings', 'showDataLabels'], widget)
    const format = R.pathOr(false, ['widgetRenderSettings', 'format'], widget)
    const currency = R.pathOr('USD', ['widgetRenderSettings', 'currency'], widget)
    const decimals = R.pathOr(0, ['widgetRenderSettings', 'dataDecimals'], widget)
    const formatter = labelFormatter(
        format === 'currency' ? dashStore.getCurrencySymbol(currency) : format,
        showDataLabels,
        false,
        decimals,
    )
    const showYAxis = R.pathOr(true, ['widgetRenderSettings', 'showYAxis'], widget)
    const showTitle = R.pathOr(false, ['widgetRenderSettings', 'showTitle'], widget)
    const title = R.pathOr(false, ['widgetRenderSettings', 'title'], widget)
    const axisOverflow = rotateX ? 'truncate' : 'break'
    const boundaryGap = R.pathOr(true, ['widgetRenderSettings', 'boundaryGap'], widget)
    const swapSeriesXAxis = R.pathOr(false, ['widgetRenderSettings', 'swapSeriesXAxis'], widget)
    const colors = R.pathOr(defaultGlobalColours, ['widgetRenderSettings', 'colors'], widget)
    const hasColourOverrides = !!R.pathOr(false, ['widgetRenderSettings', 'colors'], widget)
    const labelPosition = R.pathOr(isLine ? 'top' : 'inside', ['widgetRenderSettings', 'labelPosition'], widget)
    const groupByTags = !!widget.customTag
    const defaultLabelConfig = {
        show: showDataLabels,
        position: labelPosition,
        fontWeight: 700,
        lineHeight: 18,
        fontSize: 12,
        formatter,
    }

    if (isBar) {
        layoutEdits.push({
            path: ['yAxis', 'axisLabel', 'fontSize'],
            value: 12,
        })
    }

    layoutEdits.push(
        {
            path: ['xAxis', 'axisLabel', 'fontSize'],
            value: 10,
        },
        {
            path: ['xAxis', 'axisLabel', 'color'],
            value: 'black',
        },
        {
            path: [xaxis, 'axisLabel', 'interval'],
            value: 0,
        },
        {
            path: [xaxis, 'axisLabel', 'overflow'],
            value: isBar ? undefined : axisOverflow,
        },
        {
            path: [xaxis, 'axisLabel', 'width'],
            value: rotateX ? 140 : 100,
        },
        {
            path: [xaxis, 'axisLabel', 'rotate'],
            value: rotateX ? 45 : 0,
        },
        {
            path: [xaxis, 'boundaryGap'],
            value: boundaryGap,
        },
        {
            path: ['legend', 'show'],
            value: legendSetting,
        },
        {
            path: ['legend', 'type'],
            value: 'scroll',
        },
        {
            path: ['legend', 'itemGap'],
            value: 25,
        },
        {
            path: ['grid'],
            value: {
                containLabel: true,
                top: showTitle ? 50 : 20,
                bottom: legendSetting ? 30 : 10,
                left: rotateX ? 20 : 10,
                right: !rotateX ? 40 : 5,
            },
        },
        {
            path: [yaxis, 'axisLabel', 'show'],
            value: showYAxis,
        },
        {
            path: ['title'],
            value: {
                text: R.pathOr(R.pathOr({ text: '' }, ['en_GB'], title), [langcode], title).text,
                left: 'left',
                show: showTitle,
                textStyle: {
                    fontWeight: 'bold',
                    fontSize: 16,
                    color: '#102E4A',
                },
            },
        },
    )

    // BENCHMARK LAYOUT FORMATTING
    if (fData && !isStacked) {
        const benchmarkfDataIndices = new Set() // flag fData[i] containing benchmarks (i)
        const benchmarkfDataDataIndices = new Set() // fData[i].data[j] containing benchmarks (j)
        const benchmarkData = [] // easier access to benchmark data on layoutEdits

        fData.forEach((series, i) => {
            series.data?.forEach((d, idx) => {
                if (d?.benchmark) {
                    benchmarkfDataIndices.add(idx)
                    benchmarkfDataDataIndices.add(i)
                    benchmarkData.push({
                        data: d,
                        label: d.benchmarkName || d.name,
                    })
                }
            })
        })

        if (benchmarkData.length > 0) {
            // build axis without benchmark xAxisLabels
            const keepIndices = xAxisLabels
                .map((_, i) => i)
                .filter((i) => !benchmarkfDataIndices.has(i))
            const cleanXAxisLabels = keepIndices.map((i) => xAxisLabels[i])

            if (cleanXAxisLabels.length > 0) {
                // means there are benchmarks to be formatted
                const isRegularSeries = (series) => keepIndices.some((idx) => {
                    const hasData = series.data?.[idx]
                    const isNotBenchmarkData = !series.data[idx]?.benchmark
                    const hasRealValue = series.data?.[idx]?.value != null
                    return hasData && isNotBenchmarkData && hasRealValue
                })

                if (swapSeriesXAxis) {
                    // each benchmark should become a new x-axis-label group (not series)
                    // for this to happen, non-benchmark data get the benchmark values appended as extra data points
                    const uniqueBenchmarkLabels = [...new Set(benchmarkData.map(({ label }) => label))]
                    const newXAxisLabels = [...uniqueBenchmarkLabels, ...cleanXAxisLabels]
                    layoutEdits.push({ path: [xaxis, 'data'], value: newXAxisLabels })

                    const sortedBenchmarkIndices = [...benchmarkfDataIndices].sort((a, b) => a - b)
                    const regularSeries = fData.filter(isRegularSeries)
                    regularSeries.forEach((series, rIdx) => {
                        const cleanData = keepIndices.map((idx) => series.data[idx])
                        const seriesHasOwnBenchmarks = sortedBenchmarkIndices.some(
                            // true when this series already holds its own benchmark values at the benchmark
                            // positions (e.g. after the dataFormatter groups by benchmarkName)
                            (idx) => series.data?.[idx]?.benchmark && series.data[idx]?.value != null,
                        )

                        // benchmarkData is distributed along [data1_b1, data1_b2, data2_b1, data2_b2, ...]
                        // For rIdx=1 && numOfBenchmarks=2 => bIdx 2 and 3
                        const numOfBenchmarks = uniqueBenchmarkLabels.length
                        const benchmarkDataPoints = seriesHasOwnBenchmarks
                            ? sortedBenchmarkIndices.map((idx) => ({ ...series.data[idx] }))
                            : uniqueBenchmarkLabels.map((_, labelIdx) => ({
                                ...benchmarkData[rIdx * numOfBenchmarks + labelIdx].data,
                            }))
                        layoutEdits.push({
                            path: ['series', rIdx],
                            value: {
                                name: series.name,
                                data: [...benchmarkDataPoints, ...cleanData], // benchmarks first
                                type: isLine ? 'line' : 'bar',
                                label: defaultLabelConfig,
                                emphasis: { focus: 'series' },
                                stack: isStacked ? 'data' : undefined,
                            },
                        })
                    })

                    for (let i = regularSeries.length; i < fData.length; i += 1) {
                        // can't edit fData here, so nullifying extra values at new x-axis-label groups -- empty
                        // bars in benchmarks and duplicated data in regularSeries
                        layoutEdits.push({ path: ['series', i], value: null })
                    }
                } else {
                    if (cleanXAxisLabels.length < xAxisLabels.length) {
                        // push cleanXAxisLabels to layoutEdits so there is no standalone benchmark x-axis-label
                        layoutEdits.push({ path: [xaxis, 'data'], value: cleanXAxisLabels })
                    }

                    // group benchmarkData by label so that multiQuestion benchmarks
                    // produce ONE series per benchmark type with per-question values
                    const benchmarkGroups = benchmarkData.reduce((groups, { data, label }) => ({
                        ...groups,
                        [label]: [...(groups[label] || []), data],
                    }), {})
                    const uniqueBenchmarkGroupLabels = Object.keys(benchmarkGroups)

                    // process benchmark series: add them in the first positions
                    uniqueBenchmarkGroupLabels.forEach((label, bIdx) => {
                        const groupData = benchmarkGroups[label]
                        layoutEdits.push({
                            path: ['series', bIdx],
                            value: {
                                name: label,
                                // Map each clean label to its benchmark value
                                // 1:1 when lengths match, else match by the question name suffix that both labels share
                                data: (() => {
                                    if (groupData.length === cleanXAxisLabels.length) {
                                        return groupData.map((d, i) => ({ ...d, name: cleanXAxisLabels[i] }))
                                    }
                                    return cleanXAxisLabels.map((xLabel) => {
                                        const qSuffix = xLabel.split(' - ').slice(1).join(' - ')
                                        const match = groupData.find((d) => (
                                            d.name.split(' - ').slice(1).join(' - ') === qSuffix
                                        ))
                                        return { ...(match ?? groupData[0]), name: xLabel }
                                    })
                                })(),
                                type: isLine ? 'line' : 'bar',
                                label: { ...defaultLabelConfig, show: isLine ? false : showDataLabels },
                                emphasis: { focus: 'series' },
                                stack: undefined,
                                color: getColour(label.replace('Survey (', '').replace('Comparison (', ''), bIdx),
                            },
                        })
                    })
                    // process regular data series: remove benchmark placeholder positions and add them after
                    fData
                        .filter(isRegularSeries)
                        .forEach((series, rIdx) => {
                            const cleanData = keepIndices.map((idx) => series.data[idx])
                            layoutEdits.push({
                                path: ['series', uniqueBenchmarkGroupLabels.length + rIdx],
                                value: {
                                    name: series.name,
                                    data: cleanData,
                                    type: isLine ? 'line' : 'bar',
                                    label: defaultLabelConfig,
                                    emphasis: { focus: 'series' },
                                    stack: isStacked ? 'data' : undefined,
                                    color: getColour(series.name.replace('Survey (', '').replace('Comparison (', ''), benchmarkData.length + rIdx),
                                },
                            })
                        })
                }
            }
        } else if (fData && !hasColourOverrides) {
            fData.forEach((fd, i) => {
                layoutEdits.push({
                    path: ['series', i, 'color'],
                    value: getColour(fd?.name?.replace('Survey (', '').replace('Comparison (', ''), i),
                })
            })
        }
    }

    if (!R.isNil(widget.min)) {
        layoutEdits.push({
            path: [yaxis, 'min'],
            value: widget.min,
        })
    }

    if (!R.isNil(widget.max)) {
        layoutEdits.push({
            path: [yaxis, 'max'],
            value: widget.max,
        })
    }

    if (colors) {
        layoutEdits.push({
            path: ['color'],
            value: colors,
        })
    }

    // default template applied to every new series (Bar component)
    const newSeries = {
        type: isLine ? 'line' : 'bar',
        label: {
            show: showDataLabels,
            overflow: 'break',
            position: R.pathOr(isLine ? 'top' : 'inside', ['widgetRenderSettings', 'labelPosition'], widget),
            fontFamily: '"Noto Sans", "Noto Sans SC", "Noto Sans Arabic", sans-serif',
            fontWeight: 700,
            lineHeight: 18,
            fontSize: 12,
            formatter,
        },
        emphasis: {
            focus: 'series',
        },
        stack: isStacked ? 'data' : undefined,
        data: [],
    }
    // add background highlight if customTags are present
    const markAreaSeries = 0
    let markAreaData
    if (groupByTags
        && (
            (['avg', 'nps'].includes(widget.kpiType) && (!swapSeriesXAxis))
            || (
                !['avg', 'nps'].includes(widget.kpiType) && widget.nonQuestionType !== 'KPI'
                && ((!!swapSeriesXAxis && !isStacked) || (isStacked && !swapSeriesXAxis))
            )
            || (
                widget.nonQuestionType === 'KPI' && !widget.isMatrixKpi
                && R.pathOr('', ['dataDetails', 'type'], widget) === 'avg' && !swapSeriesXAxis
            )
        )
    ) {
        let previousGroup = ''
        let grey = true
        markAreaData = xAxisLabels.reduce((markAreaD, xAxisItem, i) => {
            const tagOptionName = xAxisItem.split(' - ').pop()
            if (tagOptionName !== previousGroup) {
                grey = !grey
                previousGroup = tagOptionName
                const newElement = [
                    {
                        name: tagOptionName,
                        xAxis: xAxisLabels[i],
                        itemStyle: { color: grey ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0)' },
                    },
                    { xAxis: xAxisLabels[i] },
                ]
                return [...markAreaD, newElement]
            }
            let lastElement = markAreaD.pop()
            lastElement = [lastElement[0], { xAxis: xAxisLabels[i] }]
            previousGroup = tagOptionName
            return [...markAreaD, lastElement]
        }, [])

        layoutEdits.push({
            path: ['series', markAreaSeries, 'markArea'],
            value: {
                label: {
                    show: true,
                    position: 'top',
                    color: '#333',
                    fontWeight: 'bold',
                },
                tooltip: { show: false },
                data: markAreaData,
            },
        })
    }

    return { layoutEdit: layoutEdits, defaultSeries: newSeries, finalxAxisLabels: xAxisLabels }
}

const radarRenderSettings = (widget, dashStore, xAxisLabels, fData) => {
    const { appStore } = dashStore
    const { langcode } = appStore
    const layoutEdits = []
    const showTitle = R.pathOr(false, ['widgetRenderSettings', 'showTitle'], widget)
    const title = R.pathOr(false, ['widgetRenderSettings', 'title'], widget)
    const legendSetting = R.pathOr(true, ['widgetRenderSettings', 'showLegend'], widget)
    const areaFill = R.pathOr(false, ['widgetRenderSettings', 'areaFill'], widget)
    const showDataLabels = R.pathOr(false, ['widgetRenderSettings', 'showDataLabels'], widget)
    const format = R.pathOr(false, ['widgetRenderSettings', 'format'], widget)
    const currency = R.pathOr('USD', ['widgetRenderSettings', 'currency'], widget)
    const decimals = R.pathOr(0, ['widgetRenderSettings', 'dataDecimals'], widget)
    const formatter = labelFormatter(
        format === 'currency' ? dashStore.getCurrencySymbol(currency) : format,
        showDataLabels,
        false,
        decimals,
    )

    const layout = [

        {
            path: ['legend', 'bottom'],
            value: 10,
        },
        {
            path: ['legend', 'show'],
            value: legendSetting,
        },
        {
            path: ['title'],
            value: {
                text: R.pathOr(R.pathOr({ text: '' }, ['en_GB'], title), [langcode], title).text,
                left: 'left',
                show: showTitle,
                textStyle: {
                    fontWeight: 'bold',
                    fontSize: 16,
                    color: '#102E4A',
                },
            },
        },
        {
            path: ['radar', 'axisLabel'],
            value: {
                show: showDataLabels,
                formatter,
            },
        },
    ]

    if (!legendSetting) {
        layoutEdits.push(
            {
                path: ['radar', 'radius'],
                value: '75%',
            },
            {
                path: ['radar', 'center'],
                value: ['50%', '55%'],
            },
        )
    }

    if (areaFill) {
        layoutEdits.push(...fData.map((d, i) => (
            {
                path: ['series', i, 'areaStyle'],
                value: {},
            }
        )))
    }

    layoutEdits.push(...layout)

    return { layoutEdit: layoutEdits }
}

const treemapRenderSettings = (widget, dashStore) => {
    const { appStore } = dashStore
    const { langcode } = appStore
    const layoutEdits = []

    const showDataLabels = R.pathOr(false, ['widgetRenderSettings', 'showDataLabels'], widget)
    const showOptionLabels = R.pathOr(true, ['widgetRenderSettings', 'showOptionLabels'], widget)
    const format = R.pathOr(false, ['widgetRenderSettings', 'format'], widget)
    const currency = R.pathOr('USD', ['widgetRenderSettings', 'currency'], widget)
    const decimals = R.pathOr(0, ['widgetRenderSettings', 'dataDecimals'], widget)
    const formatter = labelFormatter(
        format === 'currency' ? dashStore.getCurrencySymbol(currency) : format,
        showDataLabels,
        showOptionLabels,
        decimals,
    )
    layoutEdits.push(
        { path: ['series', 0, 'roam'], value: false },
        { path: ['series', 0, 'nodeClick'], value: false },
        {
            path: ['series', 0, 'label'],
            value: {
                show: showDataLabels || showOptionLabels,
                position: 'insideBottomLeft',
                fontFamily: '"Noto Sans", "Noto Sans SC", "Noto Sans Arabic", sans-serif',
                fontWeight: 700,
                fontSize: 12,
                formatter,
            },
        },
    )
    const showTitle = R.pathOr(false, ['widgetRenderSettings', 'showTitle'], widget)
    const title = R.pathOr(false, ['widgetRenderSettings', 'title'], widget)

    if (showTitle) {
        layoutEdits.push(
            {
                path: ['title'],
                value: {
                    text: R.pathOr(R.pathOr({ text: '' }, ['en_GB'], title), [langcode], title).text,
                    left: 'left',
                    show: true,
                    textStyle: {
                        fontWeight: 'bold',
                        fontSize: 16,
                        color: '#102E4A',
                    },
                },
            },
        )
    }

    return { layoutEdit: layoutEdits }
}

const quadrantAnalysisRenderSettings = (widget, dashStore, originalxAxisLabels, fData) => {
    const { appStore } = dashStore
    const { langcode } = appStore
    const layoutEdits = []
    const showTitle = R.pathOr(false, ['widgetRenderSettings', 'showTitle'], widget)
    const title = R.pathOr(false, ['widgetRenderSettings', 'title'], widget)
    const legendSetting = R.pathOr(true, ['widgetRenderSettings', 'showLegend'], widget)
    const showLabels = R.pathOr(false, ['widgetRenderSettings', 'showLabels'], widget)
    const xAxisLabels = { ...originalxAxisLabels }
    const { surveyType } = widget

    if (surveyType === 'EM') {
        xAxisLabels.x = R.or(xAxisLabels.x, '').split(' - ').slice(0, -1).join(' - ')
        xAxisLabels.y = R.or(xAxisLabels.y, '').split(' - ').slice(0, -1).join(' - ')
    }

    const layout = [
        {
            path: ['toolbox', 'feature', 'dataZoom', 'icon'],
            value: {
                zoom: 'path://M21.375,14h-4.75V9.25a1.188,1.188,0,0,0-2.375,0V14H9.5a1.188,1.188,0,0,0,0,2.375h4.75v4.75a1.188,1.188,0,0,0,2.375,0v-4.75h4.75a1.188,1.188,0,0,0,0-2.375ZM37.651,35.724l-10.5-10.5A15.426,15.426,0,1,0,25.472,26.9l10.5,10.5a1.2,1.2,0,0,0,.839.345,1.187,1.187,0,0,0,.839-2.026ZM15.438,28.25A13.063,13.063,0,1,1,28.5,15.188,13.039,13.039,0,0,1,15.438,28.25Z',
                back: 'path://M21.375,14H9.5a1.188,1.188,0,0,0,0,2.375H21.375a1.188,1.188,0,0,0,0-2.375ZM37.651,35.724l-10.5-10.5A15.426,15.426,0,1,0,25.472,26.9l10.5,10.5a1.2,1.2,0,0,0,.839.345,1.187,1.187,0,0,0,.839-2.026ZM15.438,28.25A13.063,13.063,0,1,1,28.5,15.188,13.039,13.039,0,0,1,15.438,28.25Z',
            },
        },
        { path: ['grid', 'bottom'], value: '20%' }, { path: ['grid', 'left'], value: '7%' }, { path: ['xAxis', 'name'], value: xAxisLabels.x },
        { path: ['xAxis', 'nameLocation'], value: 'middle' },
        { path: ['xAxis', 'nameGap'], value: 27 }, { path: ['yAxis', 'name'], value: xAxisLabels.y },
        { path: ['xAxis', 'nameTextStyle'], value: { fontSize: 14 } },
        { path: ['yAxis', 'nameGap'], value: 80 },
        { path: ['yAxis', 'nameTextStyle'], value: { fontSize: 14 } }, { path: ['yAxis', 'nameLocation'], value: 'middle' },
        {
            path: ['legend', 'bottom'],
            value: 10,
        },
        {
            path: ['legend', 'type'],
            value: 'scroll',
        },
        {
            path: ['legend', 'show'],
            value: legendSetting,
        },
        {
            path: ['yAxis', 'scale'],
            value: true,
        },
        {
            path: ['xAxis', 'scale'],
            value: true,
        },
        {
            path: ['yAxis', 'max'],
            value: null,
        },
        {
            path: ['xAxis', 'max'],
            value: null,
        },
        {
            path: ['yAxis', 'minInterval'],
            value: 1,
        },
        {
            path: ['xAxis', 'minInterval'],
            value: 1,
        },
        {
            path: ['title'],
            value: {
                text: R.pathOr(R.pathOr({ text: '' }, ['en_GB'], title), [langcode], title).text,
                left: 'left',
                show: showTitle,
                textStyle: {
                    fontWeight: 'bold',
                    fontSize: 16,
                    color: '#102E4A',
                },
            },
        },
    ]

    if (!showLabels) {
        layout.push({ path: ['grid', 'right'], value: '5%' })
    }

    if (fData) {
        const seriesSet = new Set()
        fData.forEach((d) => seriesSet.add(d.series))
        const seriesItems = [...seriesSet].sort()
        seriesItems.forEach((s, i) => {
            layoutEdits.push({
                path: ['series', i, 'color'],
                value: getColour(s, i),
            })
        })
    }

    layoutEdits.push(...layout)

    return { layoutEdit: layoutEdits }
}

const gaugeRenderSettings = (widget, dashStore, xAxisLabels, data) => {
    const { appStore } = dashStore
    const { langcode } = appStore
    const {
        chartType, percentiles, max, kpiType,
    } = widget
    const { benchmarkData, sortedData } = data
    const layoutEdits = []
    const showTitle = R.pathOr(false, ['widgetRenderSettings', 'showTitle'], widget)
    const title = R.pathOr(false, ['widgetRenderSettings', 'title'], widget)
    const decimals = R.pathOr(0, ['widgetRenderSettings', 'dataDecimals'], widget)
    const chartCenterData = Number(R.pathOr(
        R.pathOr(undefined, ['sortedData', 0, 'data', 0, 'value'], data),
        ['sortedData', 0, 'value'],
        data,
    ))
    const mainDataYear = R.pathOr(0, ['sortedData', 0, 'year'], data)

    const centerText = (mainDataYear
        ? (
            `${mainDataYear < 1900 ? 'Last' : 'Year:'} ${mainDataYear} ${mainDataYear < 1900 ? 'months' : ''}`
        )
        : '')

    const layout = [
        {
            path: ['title'],
            value: {
                text: R.pathOr(R.pathOr({ text: '' }, ['en_GB'], title), [langcode], title).text,
                left: 'left',
                show: showTitle,
                textStyle: {
                    fontWeight: 'bold',
                    fontSize: 16,
                    color: '#102E4A',
                },
            },
        },
        {
            path: ['series', 0, 'detail', 'rich', 'a', 'fontSize'],
            value: decimals > 0 ? 50 : 75,
        },
    ]

    layoutEdits.push(...layout)

    let yPercentage = 100
    benchmarkData.forEach((d, i) => {
        // The index + 1 in the path is becuase the first series is always the main data
        let icon = '─'
        let iconColor = '#FEB200'
        if (d.value !== sortedData[0].value) {
            icon = sortedData[0].value > d.value ? '↗' : '↘'
            iconColor = sortedData[0].value > d.value ? '#16D2C6' : '#F46957'
        }
        const isEven = i % 2 === 0
        layoutEdits.push(
            {
                path: ['series', 0, 'data', i + 1],
                value: {
                    itemStyle: { opacity: 0 },
                    detail: {
                        offsetCenter: [isEven ? '-85%' : '85%', `${yPercentage}%`],
                        formatter: `${d.benchmarkName || d.year}: ${d.value} {c|${icon}}`,
                        rich: {
                            c: {
                                color: iconColor,
                                fontSize: 11,
                                fontWeight: 700,
                            },
                        },
                        fontSize: 11,
                        width: 120,
                        lineHeight: 14,
                        color: 'black',
                        backgroundColor: '#F7F8FA',
                        borderRadius: 3,
                        fontWeight: 600,
                    },
                },
            },
        )
        if (!isEven) yPercentage += 40
    })

    if (percentiles) {
        const maxValue = kpiType === 'nps' ? 200 : max
        const additionalPercentileVal = kpiType === 'nps' ? 100 : 0
        const axisLineColors = []
        axisLineColors.push(...[
            [(percentiles.s0 + additionalPercentileVal) / maxValue, '#bdbfbe'],
            [(percentiles.s20 + additionalPercentileVal) / maxValue, '#D9314B'],
            [(percentiles.s40 + additionalPercentileVal) / maxValue, '#FEB200'],
            [(percentiles.s60 + additionalPercentileVal) / maxValue, '#FFDA10'],
            [(percentiles.s80 + additionalPercentileVal) / maxValue, '#97D100'],
            [(percentiles.s100 + additionalPercentileVal) / maxValue, '#16CCC1'],
            [1, '#bdbfbe'],
        ])

        layoutEdits.push({
            path: ['series', 0, 'axisLine', 'lineStyle', 'color'],
            value: axisLineColors,
        })
    }

    if (isNaN(chartCenterData)) {
        layoutEdits.push(...[{
            path: ['series', 0, 'detail', 'formatter'],
            value: `{a|No data}\n{a|available}\n{b|${centerText}}`,
        },
        {
            path: ['series', 0, 'detail', 'width'],
            value: 30,
        },
        {
            path: ['series', 0, 'detail', 'offsetCenter'],
            value: [0, 0],
        },
        {
            path: ['series', 0, 'detail', 'rich', 'a'],
            value: {
                fontSize: 25,
                fontWeight: 700,
                width: 30,
                color: '#102E4A',
                align: 'center',
            },
        },
        {
            path: ['series', 0, 'pointer', 'show'],
            value: false,
        },
        ])
    }

    return { layoutEdit: layoutEdits }
}

const barLineComboRenderSettings = (widget, dashStore, xAxisLabels, data, parentData) => {
    const {
        appStore,
    } = dashStore
    const { langcode } = appStore
    const { chartType } = widget
    const layoutEdits = []
    const isBar = false
    const isLine = chartType === 'line'
    const isStacked = chartType.includes('stacked')
    const rotateX = R.pathOr(!isBar && xAxisLabels?.length > 8, ['widgetRenderSettings', 'rotateXAxisLabels'], widget)
    const legendSetting = R.pathOr(true, ['widgetRenderSettings', 'showLegend'], widget)
    const showDataLabels = R.pathOr(false, ['widgetRenderSettings', 'showDataLabels'], widget)
    const format = R.pathOr(false, ['widgetRenderSettings', 'format'], widget)
    const currency = R.pathOr('USD', ['widgetRenderSettings', 'currency'], widget)
    const decimals = R.pathOr(0, ['widgetRenderSettings', 'dataDecimals'], widget)
    const formatter = labelFormatter(
        format === 'currency' ? dashStore.getCurrencySymbol(currency) : format,
        showDataLabels,
        false,
        decimals,
    )
    const percentageFormatter = labelFormatter(
        '%',
        showDataLabels,
        false,
        decimals,
    )
    const showTitle = R.pathOr(false, ['widgetRenderSettings', 'showTitle'], widget)
    const title = R.pathOr(false, ['widgetRenderSettings', 'title'], widget)
    const axisOverflow = rotateX ? 'truncate' : 'break'
    const boundaryGap = R.pathOr(true, ['widgetRenderSettings', 'boundaryGap'], widget)
    const colors = R.pathOr(defaultGlobalColours, ['widgetRenderSettings', 'colors'], widget)

    if (parentData.length) {
        parentData.forEach((pd, i) => {
            layoutEdits.push({
                path: ['series', i],
                value: {
                    data: pd.data,
                    name: `${pd.name} ${widget.parentTitle}`,
                    type: 'bar',
                    emphasis: {
                        focus: 'series',
                    },
                    stack: undefined,
                    yAxisIndex: 0,
                    label: {
                        show: showDataLabels,
                        overflow: 'break',
                        position: R.pathOr(isLine ? 'top' : 'inside', ['widgetRenderSettings', 'labelPosition'], widget),
                        fontFamily: '"Noto Sans", "Noto Sans SC", "Noto Sans Arabic", sans-serif',
                        fontWeight: 700,
                        lineHeight: 18,
                        fontSize: 12,
                        formatter: percentageFormatter,
                    },
                },
            })
        })
        data.forEach((d, i) => {
            layoutEdits.push({
                path: ['series', parentData.length + i],
                value: {
                    data: d.data,
                    name: `${d.name} ${widget.dataTitle}`,
                    type: 'line',
                    emphasis: {
                        focus: 'series',
                    },
                    stack: undefined,
                    yAxisIndex: 1,
                    label: {
                        show: false,
                        overflow: 'break',
                        position: R.pathOr(isLine ? 'top' : 'inside', ['widgetRenderSettings', 'labelPosition'], widget),
                        fontFamily: '"Noto Sans", "Noto Sans SC", "Noto Sans Arabic", sans-serif',
                        fontWeight: 700,
                        lineHeight: 18,
                        fontSize: 12,
                        formatter,
                    },
                },
            })
        })

        layoutEdits.push({
            path: ['yAxis'],
            value: [
                {
                    type: 'value',
                    min: 0,
                    max: 100,
                    interval: 20,
                    axisLabel: {
                        formatter: '{value}%',
                        fontSize: 14,
                        color: '#8B99A7',
                    },
                },
                {
                    type: 'value',
                    min: 0,
                    max: 5,
                    interval: 1,
                    axisLabel: {
                        formatter: '{value}',
                        fontSize: 14,
                        color: '#8B99A7',
                    },
                },
            ],
        })
    }

    layoutEdits.push(
        {
            path: ['xAxis', 'axisLabel', 'fontSize'],
            value: 10,
        },
        {
            path: ['xAxis', 'axisLabel', 'color'],
            value: 'black',
        },
        {
            path: ['xAxis', 'axisLabel', 'interval'],
            value: 0,
        },
        {
            path: ['xAxis', 'axisLabel', 'overflow'],
            value: isBar ? undefined : axisOverflow,
        },
        {
            path: ['xAxis', 'axisLabel', 'width'],
            value: rotateX ? 140 : 100,
        },
        {
            path: ['xAxis', 'axisLabel', 'rotate'],
            value: rotateX ? 45 : 0,
        },
        {
            path: ['xAxis', 'boundaryGap'],
            value: boundaryGap,
        },
        {
            path: ['legend', 'show'],
            value: legendSetting,
        },
        {
            path: ['legend', 'type'],
            value: 'scroll',
        },
        {
            path: ['legend', 'itemGap'],
            value: 25,
        },
        {
            path: ['grid'],
            value: {
                containLabel: true,
                top: showTitle ? 50 : 20,
                bottom: legendSetting ? 30 : 10,
                left: rotateX ? 20 : 10,
                right: !rotateX ? 40 : 5,
            },
        },
        {
            path: ['title'],
            value: {
                text: R.pathOr(R.pathOr({ text: '' }, ['en_GB'], title), [langcode], title).text,
                left: 'left',
                show: showTitle,
                textStyle: {
                    fontWeight: 'bold',
                    fontSize: 16,
                    color: '#102E4A',
                },
            },
        },
    )

    const newSeries = {
        type: 'bar',
        label: {
            show: false,
            overflow: 'break',
            position: R.pathOr(isLine ? 'top' : 'inside', ['widgetRenderSettings', 'labelPosition'], widget),
            fontFamily: '"Noto Sans", "Noto Sans SC", "Noto Sans Arabic", sans-serif',
            fontWeight: 700,
            lineHeight: 18,
            fontSize: 12,
            formatter,
        },
        emphasis: {
            focus: 'series',
        },
        stack: isStacked ? 'data' : undefined,
        data: [],
    }

    if (colors) {
        layoutEdits.push({
            path: ['color'],
            value: colors,
        })
    }
    const finalxAxisLabels = xAxisLabels

    return { layoutEdit: layoutEdits, defaultSeries: newSeries, finalxAxisLabels }
}

const chartRenderSettings = (widget, dashStore, xAxisLabels, fData, parentdata) => {
    const { chartType } = widget

    const renderFunc = {
        pie: pieRenderSettings,
        donut: pieRenderSettings,
        column: columnRenderSettings,
        bar: columnRenderSettings,
        stackedColumn: columnRenderSettings,
        stackedBar: columnRenderSettings,
        line: columnRenderSettings,
        radar: radarRenderSettings,
        treemap: treemapRenderSettings,
        quadrantAnalysis: quadrantAnalysisRenderSettings,
        gauge: gaugeRenderSettings,
        'gauge0-5': gaugeRenderSettings,
        barLineCombo: barLineComboRenderSettings,
    }[chartType]

    return renderFunc ? renderFunc(widget, dashStore, xAxisLabels, fData, parentdata) : { layoutEdit: [] }
}

export default chartRenderSettings
